import { PRESSURE_REF_TICKS, SPEED_MAX_REF, THICKNESS_MAX_REF } from '../render/avalancheColors.ts';
import { SPRITE_DENSITY } from '../render/avalancheParticles.ts';
import {
  colorMode,
  hideParticles,
  playback,
  runs,
  setPlaybackPlaying,
  setPlaybackTime,
  showDeposited,
  type ParticleColorMode,
} from '../state/avalanche.ts';
import { uiMode } from '../state/store.ts';
import { formatNumber } from './format.ts';
import { SpeedPresetButtons } from './SpeedPresetButtons.tsx';

const COLOR_MODES: { mode: ParticleColorMode; label: string }[] = [
  { mode: 'speed', label: 'Speed' },
  { mode: 'thickness', label: 'Thickness' },
  { mode: 'pressure', label: 'Pressure' },
  { mode: 'origin', label: 'Origin' },
  { mode: 'moving', label: 'Moving/stopped' },
];

function ColorLegend({ mode }: { mode: ParticleColorMode }) {
  switch (mode) {
    case 'speed':
      return (
        <div class="playback-legend-scale">
          <div class="playback-legend-ramp playback-legend-ramp-speed" />
          <div class="playback-legend-ramp-labels">
            <span>0</span>
            <span>{SPEED_MAX_REF} m/s</span>
          </div>
        </div>
      );
    case 'thickness':
      return (
        <div class="playback-legend-scale">
          <div class="playback-legend-ramp playback-legend-ramp-thickness" />
          <div class="playback-legend-ramp-labels">
            <span>0</span>
            <span>{THICKNESS_MAX_REF} m</span>
          </div>
        </div>
      );
    case 'pressure':
      return (
        <div class="playback-legend-scale">
          <div class="playback-legend-ramp playback-legend-ramp-pressure" />
          <div class="playback-legend-ramp-labels">
            <span>0</span>
            <span>{PRESSURE_REF_TICKS.windows} windows</span>
            <span>{PRESSURE_REF_TICKS.houses} houses</span>
            <span>{PRESSURE_REF_TICKS.forest}+ kPa forest</span>
          </div>
        </div>
      );
    case 'origin':
      return (
        <div class="playback-legend-swatches">
          <span class="playback-legend-swatch">
            <i style={{ background: '#4d80d9' }} /> Release
          </span>
          <span class="playback-legend-swatch">
            <i style={{ background: '#d94d59' }} /> Entrained
          </span>
        </div>
      );
    case 'moving':
      return (
        <div class="playback-legend-swatches">
          <span class="playback-legend-swatch">
            <i style={{ background: '#f28c26' }} /> Moving
          </span>
          <span class="playback-legend-swatch">
            <i style={{ background: '#8c9499' }} /> Stopped
          </span>
        </div>
      );
  }
}

/** Floating playback bar over the 3D view: play/pause, speed, scrub, colour mode + legend,
 * show-deposited / hide-particles toggles. Shown whenever a run with kept frames is selected. */
export function PlaybackBar() {
  const pb = playback.value;
  if (!pb) return null;
  const run = runs.value.find((r) => r.id === pb.runId);
  if (!run || run.result.frames.frames.length === 0) return null;
  const last = run.result.frames.frames.at(-1)!.t;
  const coarsened = run.params.particleMass > run.requestedParticleMass * 1.001;
  const volumeM3 = run.params.particleMass / SPRITE_DENSITY;
  const simple = uiMode.value === 'simple';

  return (
    <div class="playback-bar">
      <div class="playback-bar-row">
        <button
          type="button"
          class="icon-button playback-play"
          onClick={() => setPlaybackPlaying(!pb.playing)}
        >
          {pb.playing ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          class="field-slider playback-scrub"
          min={0}
          max={last}
          step={Math.max(0.01, last / 500)}
          value={pb.time}
          onInput={(e) => setPlaybackTime(Number((e.target as HTMLInputElement).value))}
        />
        <span class="playback-time">
          {formatNumber(pb.time, 1)} / {formatNumber(last, 1)} s
        </span>
        <SpeedPresetButtons />
        {!simple ? (
          <>
            <label class="checkbox-field playback-toggle">
              <input
                type="checkbox"
                checked={showDeposited.value}
                onChange={(e) => (showDeposited.value = (e.target as HTMLInputElement).checked)}
              />
              Show deposited
            </label>
            <label class="checkbox-field playback-toggle">
              <input
                type="checkbox"
                checked={hideParticles.value}
                onChange={(e) => (hideParticles.value = (e.target as HTMLInputElement).checked)}
              />
              Hide particles
            </label>
          </>
        ) : null}
      </div>
      <div class="playback-bar-row">
        {!simple ? (
          <div class="segmented playback-color-modes">
            {COLOR_MODES.map((c) => (
              <button
                key={c.mode}
                class={colorMode.value === c.mode ? 'active' : ''}
                onClick={() => (colorMode.value = c.mode)}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
        <ColorLegend mode={simple ? 'speed' : colorMode.value} />
        <span class="hint playback-dot-note">
          1 dot ≈ {formatNumber(volumeM3, 2)} m³ / {formatNumber(run.params.particleMass, 1)} kg
          {coarsened ? ' (coarsened to fit the particle budget)' : ''}
        </span>
      </div>
    </div>
  );
}
